/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import styled from "styled-components";
import { withSpectrumReact } from "components/shared/WithSpectrumReact";
import { AppHeader } from "components/shared/AppHeader";
import { AppFooter } from "components/shared/AppFooter";
import {
  HomeColumn,
  Splash,
  SplashBackdrop,
  SplashCopy,
  SplashIllustration,
  Subtitle,
  Title,
} from "components/shared/Splash";
import { JobsLineup } from "components/LandingPage/JobsLineup";
import { Feature } from "components/LandingPage/Feature";
import { StudioHero } from "components/LandingPage/StudioHero";

import micCheck1x from "images/home/landing/featurehighlights/mic-check-home@1x.jpg";
import micCheck2x from "images/home/landing/featurehighlights/mic-check-home@2x.jpg";
import music1x from "images/home/landing/featurehighlights/music-home@1x.jpg";
import music2x from "images/home/landing/featurehighlights/music-home@2x.jpg";
import recording1x from "images/home/landing/featurehighlights/recording-home@1x.jpg";
import recording2x from "images/home/landing/featurehighlights/recording-home@2x.jpg";
import switch1x from "images/home/landing/featurehighlights/switch-home@1x.jpg";
import switch2x from "images/home/landing/featurehighlights/switch-home@2x.jpg";
import transcript1x from "images/home/landing/featurehighlights/transcript-home@1x.jpg";
import transcript2x from "images/home/landing/featurehighlights/transcript-home@2x.jpg";
import { TemplateLineup } from "components/LandingPage/TemplateLineup";

const LandingPage: React.FC = () => {
  return (
    <>
      <PageContent>
        <AppHeader />

        <LandingSplash />
        <HomeColumn>
          <JobsLineup />
        </HomeColumn>

        <StudioHero />

        <TemplateLineup />

        <HomeColumn>
          <Feature
            image1x={switch1x}
            image2x={switch2x}
            title={I18n.t("landing.features.enhance.title")}
            body={I18n.t("landing.features.enhance.body")}
          />

          <Feature
            image1x={micCheck1x}
            image2x={micCheck2x}
            title={I18n.t("landing.features.mic_check.title")}
            body={I18n.t("landing.features.mic_check.body")}
          />

          <Feature
            image1x={transcript1x}
            image2x={transcript2x}
            title={I18n.t("landing.features.transcript.title")}
            body={I18n.t("landing.features.transcript.body")}
          />

          <Feature
            image1x={recording1x}
            image2x={recording2x}
            title={I18n.t("landing.features.recording.title")}
            body={I18n.t("landing.features.recording.body")}
          />

          <Feature
            image1x={music1x}
            image2x={music2x}
            title={I18n.t("landing.features.music.title")}
            body={I18n.t("landing.features.music.body")}
          />
        </HomeColumn>
        <AppFooter />
      </PageContent>
    </>
  );
};

const LandingSplash: React.FC = () => {
  return (
    <>
      <SplashBackdrop>
        <Splash>
          <SplashCopy>
            <Title>{I18n.t("landing.title")}</Title>
            <Subtitle>{I18n.t("landing.subtitle")}</Subtitle>
          </SplashCopy>
          <SplashIllustration />
        </Splash>
      </SplashBackdrop>
    </>
  );
};

const PageContent = styled.div`
  overflow-x: hidden;
  background-color: var(--spectrum-global-color-static-white);
  position: relative;
`;

export default withSpectrumReact(LandingPage);
